import type { ReactNode } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import Spinner from '../ui/Spinner/Spinner';
import NoJuniorAccount from './NoJuniorAccount';

/**
 * Wraps routes that require authentication.
 * Shows a full-screen spinner while validating, then either renders children,
 * explains that the login has no Junior account, or triggers the Cognito redirect.
 */
export default function AuthGuard({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, noAccount, login, logout } = useAuth();
  // Sync the user's saved theme from DB as soon as auth is confirmed.
  // isAuthenticated transitions false→true once, triggering a single GET /api/users/details.
  useTheme(isAuthenticated);

  if (isLoading) {
    return <Spinner overlay text="Authenticating…" />;
  }

  // Signed in but no Junior account: show only the message. "Back to sign in" signs out of
  // Cognito, so its session does not sign straight back in.
  if (noAccount) {
    return <NoJuniorAccount onBackToSignIn={logout} />;
  }

  if (!isAuthenticated) {
    login();
    return <Spinner overlay text="Redirecting to login…" />;
  }

  return <>{children}</>;
}
